/**
 * Cognito password login for the existing login forms (ENG-6103).
 *
 * The forms already speak one contract: the legacy Basic-auth login throws an
 * `UnauthorizedError` whose code says what to do next (`auth.mfa_required`
 * with a method and masked address, `auth.invalid_credentials`, ...). This
 * adapter drives the Cognito wrapper and answers in that same contract, so
 * each form keeps its own state machine and only swaps the call it makes.
 *
 * After Cognito completes, the viewer is fetched with the new ID token. The
 * api's MFA gate (ENG-6095) answers it with 403 `auth.mfa_setup_required` for a
 * user who had MFA before the migration and holds no Cognito factor yet: only
 * an enrolment-safe subset of fields passes, and the full viewer selects more.
 * That is raised as the same code, and the login page takes the user through
 * TOTP enrolment before anything else.
 */
import { ApolloError } from '@apollo/client';

import config from '@/config';
import { GraphQLClient, OTP_METHODS, OtpMethod, UnauthorizedError, hasErrorCode } from '@/core/api';
import ERROR_CODES, { type ErrorCode } from '@/core/apiErrorCodes';
import { Viewer, fetchViewer } from '@/core/viewer';

import { endCognitoSession, isCognitoSessionActive, onCognitoSessionEnd } from './cognitoSession';
import {
  CognitoMfaType,
  CognitoSignInError,
  CognitoSignInResult,
  TotpSetup,
  clearPendingSignIn,
  completeTotpEnrolment,
  confirmSignIn,
  selectMfaType,
  signIn,
  startTotpEnrolment,
} from './cognitoSignIn';
import { isMfaGateResponse } from './mfaGate';

interface CognitoLoginOptions {
  email: string;
  password: string;
  /** The code for the pending challenge; omit to start (or restart) sign-in. */
  otp?: string;
}

// The secret of an MFA_SETUP challenge, kept until the user answers it. Null
// when enrolment follows a completed sign-in (the api-driven case).
let pendingChallengeTotp: TotpSetup | null = null;
// The in-flight setUpTOTP: a second call (StrictMode runs effects twice) must
// show the secret Cognito kept, not mint another.
let totpSetupInFlight: Promise<TotpSetup> | null = null;
let releaseTimer: ReturnType<typeof setTimeout> | undefined;
// Cognito finished signing in but the viewer fetch after it failed (a network
// blip). The challenge is consumed, so a retry only refetches the viewer.
let signInCompleted = false;

/** Forget the sign-in and enrolment in progress. */
export function clearPendingCognitoSignIn(): void {
  clearTimeout(releaseTimer);
  signInCompleted = false;
  pendingChallengeTotp = null;
  totpSetupInFlight = null;
  clearPendingSignIn();
}

// Expiry or a logout in another tab ends the session without logout().
onCognitoSessionEnd(clearPendingCognitoSignIn);

/** Give up on a sign-in this page will not finish, and end its session. */
export function abandonCognitoSignIn(): Promise<void> {
  clearPendingCognitoSignIn();
  return endCognitoSession();
}

/**
 * The enrolment screen unmounted. Deferred a tick, and cancelled by the next
 * start, so StrictMode's unmount-and-remount keeps a live challenge.
 */
export function releaseCognitoEnrolment(): void {
  clearTimeout(releaseTimer);
  releaseTimer = setTimeout(clearPendingCognitoSignIn, 0);
}

// TOTP first: it needs no delivery, and it is the only factor the in-login
// enrolment screen sets up.
const MFA_PREFERENCE: CognitoMfaType[] = ['TOTP', 'EMAIL', 'SMS'];

function unauthorized(
  code: ErrorCode,
  message: string,
  otp?: { method: OtpMethod; maskedAddress: string }
): UnauthorizedError {
  return new UnauthorizedError(message, {
    code,
    reason: message,
    otp_method: otp?.method,
    otp_masked_address: otp?.maskedAddress,
  });
}

function mfaSetupRequired(): UnauthorizedError {
  return unauthorized(ERROR_CODES.AUTH_MFA_SETUP_REQUIRED, 'Two-factor authentication is required');
}

/** True when the api refused a request because the user must enrol MFA first. */
export function isMfaSetupRequired(err: unknown): boolean {
  if (hasErrorCode(err, ERROR_CODES.AUTH_MFA_SETUP_REQUIRED)) return true;
  return err instanceof ApolloError && isMfaGateResponse(err.networkError);
}

function fetchFullViewer(): Promise<Viewer> {
  // No explicit auth: authLink sends the active Cognito ID token.
  // The caller routes a gated user itself (enrolment on the login page, or
  // abandoning the session in a modal), so the link must not navigate.
  return fetchViewer(
    new GraphQLClient(config.apiUrl, undefined, { skipMfaGateRedirect: true }).client
  );
}

/**
 * True while the api's MFA gate refuses this session. The full viewer query
 * selects fields outside the gate's allowlist (api#1925), so it opens exactly
 * when the gate does. Any other failure rejects, and the caller decides.
 */
export async function isMfaGateClosed(): Promise<boolean> {
  try {
    await fetchFullViewer();
    return false;
  } catch (err) {
    if (isMfaSetupRequired(err)) return true;
    throw err;
  }
}

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * After enrolment: the api caches the gate's answer for up to a minute, so
 * poll until it opens. Resolves false if it is still closed at the deadline.
 */
export async function waitForMfaGateToLift({
  intervalMs = 5000,
  timeoutMs = 90000,
}: { intervalMs?: number; timeoutMs?: number } = {}): Promise<boolean> {
  const deadline = Date.now() + timeoutMs;
  for (;;) {
    // A stalled request must not outlast the deadline: count it as still closed.
    const closed = await Promise.race([
      isMfaGateClosed().catch(() => true),
      sleep(Math.max(deadline - Date.now(), 0)).then(() => true),
    ]);
    if (!closed) return true;
    if (Date.now() + intervalMs > deadline) return false;
    await sleep(intervalMs);
  }
}

async function fetchCognitoViewer(): Promise<Viewer> {
  try {
    return await fetchFullViewer();
  } catch (err) {
    if (isMfaSetupRequired(err)) throw mfaSetupRequired();
    throw err;
  }
}

function pickMfaType(allowed: CognitoMfaType[]): CognitoMfaType | undefined {
  return MFA_PREFERENCE.find((type) => allowed.includes(type));
}

async function settle(result: CognitoSignInResult): Promise<Viewer> {
  if (result.type === 'done') {
    pendingChallengeTotp = null;
    // Stays set after success too: callers fetch more (authSession.authenticate,
    // a SetupIntent) and may retry. Cleared only by a fresh password sign-in
    // or a session teardown.
    signInCompleted = true;
    return fetchCognitoViewer();
  }
  if (result.type === 'unsupported') {
    throw new Error(`Cognito sign-in stopped at an unsupported step: ${result.step}`);
  }
  switch (result.challenge) {
    case 'SOFTWARE_TOKEN_MFA':
      throw unauthorized(ERROR_CODES.AUTH_MFA_REQUIRED, 'Verification code required', {
        method: OTP_METHODS.APP,
        maskedAddress: '',
      });
    case 'SMS_MFA':
    case 'EMAIL_OTP':
      throw unauthorized(ERROR_CODES.AUTH_MFA_REQUIRED, 'Verification code required', {
        method: result.challenge === 'SMS_MFA' ? OTP_METHODS.SMS : OTP_METHODS.EMAIL,
        maskedAddress: result.destination ?? '',
      });
    case 'SELECT_MFA_TYPE':
    case 'MFA_SETUP': {
      if (result.challenge === 'MFA_SETUP' && result.totp) {
        pendingChallengeTotp = result.totp;
        throw mfaSetupRequired();
      }
      // Only TOTP can be set up here, so a setup selection always picks it.
      const choice =
        result.challenge === 'MFA_SETUP' ? 'TOTP' : pickMfaType(result.allowedMfaTypes);
      if (!choice || !result.allowedMfaTypes.includes(choice)) {
        throw new Error(`No supported MFA type in ${result.allowedMfaTypes.join(',')}`);
      }
      return settle(await selectMfaType(choice));
    }
  }
}

/** A Cognito failure as the `UnauthorizedError` the auth forms switch on; anything else unchanged. */
export function toUnauthorized(err: unknown): unknown {
  if (!(err instanceof CognitoSignInError)) return err;
  // A lapsed challenge cannot be answered again: start from the password.
  if (err.code === ERROR_CODES.AUTH_TOKEN_EXPIRED) clearPendingCognitoSignIn();
  return unauthorized(err.code, err.message);
}

/**
 * Sign in with Cognito and fetch the viewer. Throws `UnauthorizedError` with
 * the legacy codes for every outcome a form must react to.
 */
export async function cognitoPasswordLogin({
  email,
  password,
  otp,
}: CognitoLoginOptions): Promise<Viewer> {
  if (!otp) clearPendingCognitoSignIn();
  try {
    if (signInCompleted && isCognitoSessionActive()) {
      return await settle({ type: 'done' });
    }
    return await settle(otp ? await confirmSignIn(otp) : await signIn(email, password));
  } catch (err) {
    throw toUnauthorized(err);
  }
}

/** The secret to show on the enrolment screen. */
export function startCognitoTotpEnrolment(accountName: string): Promise<TotpSetup> {
  clearTimeout(releaseTimer);
  if (pendingChallengeTotp) return Promise.resolve(pendingChallengeTotp);
  if (!totpSetupInFlight) {
    totpSetupInFlight = startTotpEnrolment(accountName).catch((err: unknown) => {
      totpSetupInFlight = null;
      throw toUnauthorized(err);
    });
  }
  return totpSetupInFlight;
}

/**
 * Verify the first code. Throws `auth.otp_invalid` for a wrong code; the
 * screen stays up for another try. The api gate may stay closed for up to a
 * minute after this resolves: follow with waitForMfaGateToLift().
 */
export async function completeCognitoTotpEnrolment(code: string): Promise<void> {
  try {
    if (!pendingChallengeTotp) {
      await completeTotpEnrolment(code);
    } else {
      const result = await confirmSignIn(code);
      // Anything but DONE is a further challenge, which settle() raises.
      if (result.type !== 'done') await settle(result);
    }
    clearPendingCognitoSignIn();
  } catch (err) {
    throw toUnauthorized(err);
  }
}
